import { ImagePlus, Loader2, Star, Trash2, UploadCloud, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errorMessage } from '../../services/api.js';

const MAX_GALLERY_IMAGES = 5;

const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('alt', file.name);
  const response = await api.post('/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data.url;
};

export const ImageUploadField = ({
  label,
  value,
  onChange,
  multiple = false,
  max = MAX_GALLERY_IMAGES,
  helper,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const images = multiple ? value || [] : value ? [value] : [];

  const handleFiles = async (fileList) => {
    const incoming = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
    if (!incoming.length) return;

    if (multiple && images.length + incoming.length > max) {
      setError(t('admin.maxImages', { max }));
      return;
    }

    setUploading(true);
    setError('');
    try {
      const uploaded = [];
      for (const file of multiple ? incoming.slice(0, max - images.length) : incoming.slice(0, 1)) {
        uploaded.push(await uploadFile(file));
      }
      if (multiple) {
        onChange([...images, ...uploaded].slice(0, max));
      } else {
        onChange(uploaded[0] || '');
      }
    } catch (err) {
      setError(errorMessage(err, t('admin.uploadFailed')));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (index) => {
    if (multiple) onChange(images.filter((_, imageIndex) => imageIndex !== index));
    else onChange('');
  };

  const setCover = (index) => {
    if (!multiple || index === 0) return;
    const next = [...images];
    const [selected] = next.splice(index, 1);
    onChange([selected, ...next]);
  };

  return (
    <div className="grid gap-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-sm font-extrabold text-beam-ink">{label}</p>
          {helper ? <p className="mt-1 text-xs font-semibold text-beam-graphite/60">{helper}</p> : null}
        </div>
        {multiple ? (
          <span className="rounded-md bg-beam-light px-2 py-1 text-xs font-extrabold text-beam-blue">
            {images.length}/{max}
          </span>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        className={`focus-ring grid min-h-36 place-items-center rounded-lg border border-dashed p-5 text-center transition ${
          dragging ? 'border-beam-blue bg-beam-light' : 'border-beam-blue/20 bg-white hover:bg-beam-light'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          className="hidden"
          onChange={(event) => handleFiles(event.target.files)}
        />
        <div>
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-lg bg-beam-blue text-white">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <UploadCloud className="h-5 w-5" />}
          </div>
          <p className="mt-3 text-sm font-extrabold text-beam-ink">
            {uploading ? t('admin.uploadingImages') : t('admin.dropImages')}
          </p>
          <p className="mt-1 text-xs font-semibold text-beam-graphite/60">
            {t('admin.dropImagesHelp')}
          </p>
        </div>
      </button>

      {error ? <p className="rounded-lg bg-state-critical/10 p-3 text-sm font-bold text-state-critical">{error}</p> : null}

      {images.length ? (
        <div className={`grid gap-3 ${multiple ? 'sm:grid-cols-2 xl:grid-cols-5' : 'sm:grid-cols-[220px_1fr]'}`}>
          {images.map((image, index) => (
            <figure key={`${image}-${index}`} className="group relative overflow-hidden rounded-lg border border-beam-blue/10 bg-beam-light">
              <img src={image} alt="" className="h-40 w-full object-cover" />
              <div className="absolute inset-x-2 top-2 flex justify-between gap-2">
                {multiple ? (
                  <button
                    type="button"
                    onClick={() => setCover(index)}
                    className="focus-ring inline-flex h-9 items-center gap-1 rounded-lg bg-white/90 px-2 text-xs font-extrabold text-beam-ink shadow-sm"
                    title={index === 0 ? t('admin.coverImage') : t('admin.setAsCover')}
                  >
                    <Star className={`h-4 w-4 ${index === 0 ? 'fill-beam-yellow text-beam-yellow' : ''}`} />
                    {t('admin.cover')}
                  </button>
                ) : (
                  <span className="inline-flex h-9 items-center gap-1 rounded-lg bg-white/90 px-2 text-xs font-extrabold text-beam-ink shadow-sm">
                    <ImagePlus className="h-4 w-4" />
                    {t('admin.cover')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/90 text-state-critical shadow-sm"
                  title={t('admin.removeImage')}
                >
                  {multiple ? <Trash2 className="h-4 w-4" /> : <X className="h-4 w-4" />}
                </button>
              </div>
              <figcaption className="truncate px-3 py-2 text-xs font-semibold text-beam-graphite/70">
                {image}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : null}
    </div>
  );
};
